import { Button } from "@/components/ui/button";
import {
    ArDateFormField,
    ArForm,
    ArFormField,
    ArNumberFormField,
    ArSelectFormField,
    ArTextAreaFormField,
} from "@/v2/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import LoadingAnimation from "../../ui/LoadingAnimation";
import FindOneProject from "@/v2/api/project/FindOne";
import CreateProject from "@/v2/api/project/Create";
import UpdateProject from "@/v2/api/project/Update";
import { CreateProjectSchema } from "@/v2/schema/project";
import { ArEmployeeComboBox, ClientComboBox } from "../../ui/combo-box";
import { SelectRepresentative } from "../mobilization/SelectRepresentative";
import { Client } from "@/v2/types";
import { ArSwitchFormField } from "../../ui/form/SwitchFormField";

export default function ProjectForm({
    id,
    closeOnSave,
}: {
    id?: string;
    closeOnSave: () => void;
}) {
    const queryClient = useQueryClient();

    const [selectedClient, setSelectedClient] = useState<Client | null>();

    const query = useQuery({
        queryKey: ["projects", id],
        queryFn: () => FindOneProject(id!),
        enabled: !!id,
    });

    const form = useForm<CreateProjectSchema>({
        resolver: zodResolver(CreateProjectSchema),
        defaultValues: {
            name: "",
            location: "",
            description: "",
            start_date: "",
            end_date: null,
            client_id: "",
            client_representative: {
                id: "",
                full_name: "",
                last_name: "",
                first_name: "",
                email: "",
                phone_number: "",
            },
            client_location: "",
            client_vendor_code: "",
            client_rate: 0,
            client_commission: 0,
            reference_number: "",
            ar_coordinator_id: "",
            closed: false,
            deduction: false,
            po_number: "",
            deduction_amount: 0,
        },
    });

    const { reset, setValue, watch } = form;

    useEffect(() => {
        if (query.data) {
            setSelectedClient(query.data?.client);

            reset({
                name: query.data.name || "",
                location: query.data.location || "",

                description: query.data.description || "",
                start_date: query.data.start_date || "",
                end_date: query.data.end_date || null,
                client_id: query.data.client_id || "",
                client_representative: {
                    id: query.data.client_representative?.id ?? "",
                    full_name:
                        query.data.client_representative?.full_name ?? "",
                    last_name:
                        query.data.client_representative?.last_name ?? "",
                    first_name:
                        query.data.client_representative?.first_name ?? "",
                    email: query.data.client_representative?.email ?? "",
                    phone_number:
                        query.data.client_representative?.phone_number ?? "",
                },
                client_location: query.data.client_location || "",
                client_vendor_code: query.data.client_vendor_code || "",
                client_rate: query.data.client_rate || 0,
                client_commission: query.data.client_commission || 0,
                reference_number: query.data.reference_number || "",
                ar_coordinator_id: query.data.ar_coordinator_id || "",
                closed: query.data.closed ?? false,
                deduction: query.data.deduction || false,
                deduction_amount: query.data.deduction_amount || 0,
                po_number: query?.data?.po_number || "",
            });
        }
    }, [query.data, reset]);

    useEffect(() => {
        if (
            query?.data?.client_id !== selectedClient?.id &&
            form.formState.isDirty
        ) {
            setValue("client_location", selectedClient?.locations?.[0] || "");
        } else {
            setValue("client_location", query?.data?.client_location || "");
        }
    }, [selectedClient]);

    const CreateMutation = useMutation({
        mutationFn: (data: CreateProjectSchema) => CreateProject(data),
    });

    const EditMutation = useMutation({
        mutationFn: (data: CreateProjectSchema) =>
            UpdateProject({ id: id!, data }),
    });

    const handleFormSubmit = (data: CreateProjectSchema) => {
        const mutation = id ? EditMutation : CreateMutation;
        const action = id ? "updated" : "created";

        mutation.mutate(data, {
            onError: (err) => {
                console.error({ err });
                toast.error("Oops! Something went wrong", {
                    description: `While ${id ? "updating" : "creating"} the project, we encountered an issue.`,
                });
            },
            onSuccess: () => {
                toast.success(`Project ${action} successfully`);
                queryClient.invalidateQueries({
                    queryKey: ["projects"],
                });
                closeOnSave();
            },
        });
    };

    if (id && query.isLoading) {
        return <LoadingAnimation />;
    }

    if (id && query.isError) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="mb-4 text-red-600">Failed to load project</p>
                    <Button onClick={() => closeOnSave()}>Go Back</Button>
                </div>
            </div>
        );
    }

    const deducting = watch("deduction");

    return (
        <ArForm
            form={form}
            formSchema={CreateProjectSchema}
            onSubmit={handleFormSubmit}
        >
            <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<CreateProjectSchema>
                        label="Project Name"
                        name="name"
                        placeholder="Enter project name"
                    />
                    <ArFormField<CreateProjectSchema>
                        label="Project Location"
                        name="location"
                        placeholder="Enter project location"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArDateFormField<CreateProjectSchema>
                        label="Start Date"
                        name="start_date"
                    />
                    <ArDateFormField<CreateProjectSchema>
                        label="End Date"
                        name="end_date"
                        showClearButton
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ClientComboBox<CreateProjectSchema>
                        name="client_id"
                        setSelectedClient={setSelectedClient}
                        defaultValue={query?.data?.client || null}
                    />
                    <SelectRepresentative<CreateProjectSchema>
                        label="Client Representative"
                        name="client_representative"
                        disabled={!selectedClient?.representatives?.length}
                        options={selectedClient?.representatives ?? []}
                        defaultValue={selectedClient?.representatives?.[0]}
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArSelectFormField<CreateProjectSchema>
                        label="Client Location"
                        name="client_location"
                        disabled={!selectedClient?.locations?.length}
                        options={
                            selectedClient?.locations?.map((loc) => ({
                                label: loc,
                                value: loc,
                            })) ?? []
                        }
                        defaultValue={selectedClient?.locations?.[0]}
                    />
                    <ArFormField<CreateProjectSchema>
                        label="Vendor Code"
                        name="client_vendor_code"
                        placeholder="Enter vendor code"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArNumberFormField<CreateProjectSchema>
                        label="Client Rate"
                        name="client_rate"
                        placeholder="Enter client rate"
                    />
                    <ArNumberFormField<CreateProjectSchema>
                        label="Client Commission"
                        name="client_commission"
                        placeholder="Enter client commission"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<CreateProjectSchema>
                        label="Reference Number"
                        name="reference_number"
                        placeholder="Enter reference number"
                    />

                    <ArEmployeeComboBox<CreateProjectSchema>
                        label="AR Coordinator"
                        name="ar_coordinator_id"
                        placeholder="Select AR coordinator"
                        defaultValue={query?.data?.ar_coordinator || null}
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<CreateProjectSchema>
                        label="PO Number"
                        name="po_number"
                        placeholder="Enter purchase order number"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-1">
                    <ArTextAreaFormField<CreateProjectSchema>
                        label="Project Description"
                        name="description"
                        placeholder="Enter project description"
                    />
                </div>

                <div
                    className={`grid grid-cols-1 gap-4 ${deducting ? "md:grid-cols-2" : "md:grid-cols-1"} `}
                >
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArSwitchFormField<CreateProjectSchema>
                            label="Project Completed"
                            name="closed"
                        />

                        <ArSwitchFormField<CreateProjectSchema>
                            label="Subject to deduction"
                            name="deduction"
                        />
                    </div>

                    {deducting && (
                        <ArNumberFormField<CreateProjectSchema>
                            label="Deduction Amount"
                            name="deduction_amount"
                        />
                    )}
                </div>

                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button
                        type="submit"
                        className="min-w-[160px]"
                        disabled={
                            CreateMutation.isPending || EditMutation.isPending
                        }
                    >
                        {CreateMutation.isPending || EditMutation.isPending
                            ? "Saving..."
                            : id
                              ? "Update Project"
                              : "Create Project"}
                    </Button>
                </div>
            </div>
        </ArForm>
    );
}
